/**
 * This file once held a 473-line MODULES constant — a complete user guide for
 * an app that never shipped. It described an "Office Manager" role, "Casual,
 * Sick, Earned" leave types, "Mark Present" / "End Day" buttons, CSV exports
 * and training-video slots, none of which exist in the built product; it was
 * served verbatim to every user for months. The guide now lives in
 * `src/content/userGuide.js` as role-tagged blocks whose button names are
 * verified against the components by `src/content/userGuide.test.js` — the
 * constant was DELETED, not bypassed, so it cannot be imported into a second
 * page tomorrow (the #78 lesson, applied to prose).
 */
import { useMemo, useState } from "react";
import { BookOpen, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { GUIDE_SECTIONS, GUIDE_VERSION, ROLE_LABELS } from "../content/userGuide";
import { SIDEBAR_ITEMS } from "../components/Sidebar";

export const UserGuidePage = () => {
  const { user } = useAuth();
  const role = user?.role;

  // Sections the reader's role can actually reach, in sidebar order, each
  // holding only the blocks tagged for that role. A role outside a section's
  // nav item never sees the section at all — the guide mirrors the sidebar.
  const sections = useMemo(() => {
    const navOrder = SIDEBAR_ITEMS.filter((i) => i.roles.includes(role)).map((i) => i.to);
    return navOrder
      .map((nav) => {
        const section = GUIDE_SECTIONS.find((s) => s.nav === nav);
        if (!section) return null;
        const blocks = section.blocks.filter((b) => b.roles.includes(role));
        return blocks.length ? { ...section, blocks } : null;
      })
      .filter(Boolean);
  }, [role]);

  const [activeNav, setActiveNav] = useState(null);
  const active = sections.find((s) => s.nav === activeNav) || sections[0];

  if (!active) return null;

  return (
    <div className="flex flex-col lg:flex-row gap-6" data-testid="user-guide-page">
      <aside className="lg:w-64 flex-shrink-0">
        <div className="bg-white border border-slate-200 rounded-lg p-3 lg:sticky lg:top-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 px-2 py-2">
            User Guide
          </h2>
          <p className="px-2 pb-2 text-[11px] text-slate-400">
            For your role: {ROLE_LABELS[role] || role}
          </p>
          <nav className="space-y-1">
            {sections.map((s) => {
              const isActive = active.nav === s.nav;
              return (
                <button
                  key={s.nav}
                  onClick={() => setActiveNav(s.nav)}
                  data-testid={`guide-nav-${s.nav.replace("/", "")}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-sm transition ${
                    isActive ? "bg-callisto-navy text-white" : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <span>{s.title}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
            <BookOpen className="h-3.5 w-3.5" />
            <span>User Guide</span>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-slate-700">{active.title}</span>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{active.title}</h1>

          <div className="space-y-7">
            {active.blocks.map((b) => (
              <section key={b.id} data-testid={`guide-block-${b.id}`}>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">{b.heading}</h2>
                <p className="text-slate-700 leading-relaxed">{b.body}</p>
              </section>
            ))}
          </div>

          <p className="mt-10 pt-4 border-t border-slate-100 text-xs text-slate-400" data-testid="guide-version">
            Guide version {GUIDE_VERSION} — describes the application as built. If the screen
            disagrees with this page, the screen is newer.
          </p>
        </div>
      </main>
    </div>
  );
};
